/**
 * React Query store — admin custom fields (A6.4).
 *
 * Hooks que consomem `/api/v1/admin/custom-fields` + `/admin/custom-validators`
 * (rotas A6.3) pra alimentar `CamposPersonalizadosListPage` + `CampoBuilderDialog`.
 *
 * Path do arquivo: `src/pages/admin/custom-fields/store.ts` — namespaced
 * dentro da pasta da feature pra evitar colisão com `lib/custom-fields-store.ts`
 * (FEAT-V2-032, ConditionsBuilder de workflows — entidade diferente).
 *
 * NÃO REVERTER:
 *   - Cache key `['admin-custom-fields', filtros]` distinto do FEAT-V2-032
 *   - staleTime 30s pra listagem, 5min pra validators (raramente mudam)
 *   - Mutations invalidam apenas o batch admin
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlignLeft,
  Braces,
  Building2,
  Calculator,
  Calendar,
  CalendarClock,
  CheckSquare,
  ChevronDownSquare,
  CircleDot,
  Clock,
  DollarSign,
  Fingerprint,
  Hash,
  Image,
  Link,
  ListChecks,
  Mail,
  Paperclip,
  Percent,
  Phone,
  Type,
  type LucideIcon,
} from 'lucide-react';

import { api } from '@/lib/api';

export type ScopeTipo = 'global' | 'workflow' | 'modal' | 'pagina';
export type FieldTipo =
  | 'text' | 'textarea' | 'number' | 'currency' | 'percentage'
  | 'email' | 'phone' | 'cpf' | 'cnpj' | 'date' | 'datetime' | 'time'
  | 'dropdown' | 'multi_select' | 'checkbox' | 'radio'
  | 'file' | 'image' | 'url' | 'computed' | 'json';
export type Largura = 'full' | 'half' | 'third' | 'quarter';

export interface AdminCustomField {
  id: string;
  entidade: string;
  scopeTipo: ScopeTipo;
  scopeId: string | null;
  key: string;
  label: string;
  descricao: string | null;
  tipo: FieldTipo;
  opcoesJson: string | null;
  validacaoJson: string | null;
  computedExpression: string | null;
  defaultValue: string | null;
  visivelParaPerfisJson: string | null;
  editavelParaPerfisJson: string | null;
  placeholder: string | null;
  prefix: string | null;
  suffix: string | null;
  largura: Largura;
  ordem: number;
  grupo: string | null;
  icone: string | null;
  /** PF9 — campo aparece no formulário público de captação (0/1). */
  mostrarNoFormPublico: number;
  /** PF9 — campo é exigido no envio do formulário público (0/1). */
  obrigatorioNoFormPublico: number;
  ativo: number;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface AdminCustomValidator {
  id: string;
  nome: string;
  descricao: string | null;
  tipo: 'regex' | 'expression' | 'api_call';
  configJson: string;
  mensagemErroDefault: string | null;
  ativo: number;
}

export interface AdminCustomFieldInput {
  entidade: string;
  scopeTipo: ScopeTipo;
  scopeId?: string | null;
  key: string;
  label: string;
  descricao?: string | null;
  tipo: FieldTipo;
  opcoesJson?: string | null;
  validacaoJson?: string | null;
  computedExpression?: string | null;
  defaultValue?: string | null;
  visivelParaPerfisJson?: string | null;
  editavelParaPerfisJson?: string | null;
  placeholder?: string | null;
  prefix?: string | null;
  suffix?: string | null;
  largura?: Largura;
  ordem?: number;
  grupo?: string | null;
  icone?: string | null;
  mostrarNoFormPublico?: boolean;
  obrigatorioNoFormPublico?: boolean;
}

export interface ListFieldsFilters {
  entidade?: string;
  scopeTipo?: ScopeTipo;
  scopeId?: string;
  apenasAtivos?: boolean;
}

export function useAdminCustomFields(filters: ListFieldsFilters = {}) {
  return useQuery({
    queryKey: ['admin-custom-fields', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.entidade) params.set('entidade', filters.entidade);
      if (filters.scopeTipo) params.set('scope_tipo', filters.scopeTipo);
      if (filters.scopeId) params.set('scope_id', filters.scopeId);
      if (filters.apenasAtivos === false) params.set('apenas_ativos', 'false');
      const url = `/api/v1/admin/custom-fields${params.toString() ? `?${params}` : ''}`;
      const res = await api.get<{ items: AdminCustomField[] }>(url);
      return res.items;
    },
    staleTime: 30_000,
  });
}

export function useAdminCustomValidators() {
  return useQuery({
    queryKey: ['admin-custom-validators'],
    queryFn: async () => {
      const res = await api.get<{ items: AdminCustomValidator[] }>(
        '/api/v1/admin/custom-fields/validators',
      );
      return res.items;
    },
    staleTime: 5 * 60_000,
  });
}

export function useAdminCustomFieldsActions() {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: async (input: AdminCustomFieldInput) => {
      const res = await api.post<{ field: AdminCustomField }>('/api/v1/admin/custom-fields', input);
      return res.field;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-custom-fields'] });
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<AdminCustomFieldInput> }) => {
      const res = await api.put<{ field: AdminCustomField }>(
        `/api/v1/admin/custom-fields/${id}`,
        patch,
      );
      return res.field;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-custom-fields'] });
    },
  });

  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/admin/custom-fields/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-custom-fields'] });
    },
  });

  const duplicate = useMutation({
    mutationFn: async ({
      id,
      scopeTipo,
      scopeId,
      key,
    }: {
      id: string;
      scopeTipo: ScopeTipo;
      scopeId?: string | null;
      key?: string;
    }) => {
      const res = await api.post<{ field: AdminCustomField }>(
        `/api/v1/admin/custom-fields/${id}/duplicate`,
        { scopeTipo, scopeId, key },
      );
      return res.field;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-custom-fields'] });
    },
  });

  return { create, update, deactivate, duplicate };
}

export interface EntidadeSuportada {
  value: string;
  label: string;
  /**
   * ONDE, em português, o campo aparece pra quem vai preencher. `null` = não
   * existe tela ainda.
   *
   * Isto não é enfeite: o banco aceita `entidade` livre (nenhum CHECK), e o
   * construtor listava 10 cadastros como se todos rendessem um campo
   * preenchível. Só 4 rendem. Sem esta frase, o admin cria um campo, não vê
   * nada aparecer e conclui que o sistema está quebrado — o mesmo tipo de
   * promessa vazia que a FASE-C matou nos tipos Arquivo/Imagem.
   */
  onde: string | null;
  /** Ressalva específica do cadastro (aparece junto do `onde`). */
  nota?: string;
}

/**
 * Entidades suportadas pra dropdown do admin.
 *
 * Os 4 lugares decididos pelo Matheus em 2026-07-30
 * (`docs/architecture/CAMPOS-PERSONALIZADOS-DECISAO.md`) são `lead` (ficha
 * interna + formulário público), `cliente` e `manutencao`. O resto continua
 * ofertado — campo já criado neles não pode sumir, e `deal` recebe valor por
 * cópia na conversão do lead —, mas agora diz na cara que não tem tela.
 */
export const ENTIDADES_SUPORTADAS: ReadonlyArray<EntidadeSuportada> = [
  {
    value: 'lead',
    label: 'Lead (captação)',
    onde: 'Ficha do lead, aba Dados (Comercial › Leads).',
    nota:
      'Se você ligar "Exibir no formulário de captação" abaixo, o campo também aparece no ' +
      'formulário público — e aí quem preenche é o visitante.',
  },
  {
    value: 'cliente',
    label: 'Cliente',
    onde: 'Ficha do cliente, aba Identificação.',
    nota:
      'Campo personalizado de cliente existe só neste sistema: nunca vai para o Omie, ' +
      'nem quando a escrita no Omie for ligada.',
  },
  {
    value: 'manutencao',
    label: 'OS / Manutenção',
    onde: 'Janela da ordem de serviço, aba Detalhes (Manutenção).',
  },
  {
    value: 'deal',
    label: 'Negócio (card do funil)',
    onde: null,
    nota:
      'O card do funil ainda não desenha campos personalizados. O que existe é a conversão: ' +
      'quando um lead vira proposta, o valor do campo de lead é copiado pro campo de negócio ' +
      'com o MESMO identificador. Criar aqui só faz sentido pra receber essa cópia.',
  },
  { value: 'projeto', label: 'Projeto', onde: null },
  { value: 'chamado', label: 'Chamado', onde: null },
  { value: 'contrato', label: 'Contrato', onde: null },
  { value: 'orcamento', label: 'Orçamento', onde: null },
  { value: 'responsavel', label: 'Responsável', onde: null },
  // 'empresa_contratante' saiu em 01/08 com o conceito de cliente terceirizado.
  // Conferido na produção antes de tirar: `custom_fields` não tinha nenhum campo
  // dessa entidade (a tabela está vazia), então a opção não deixa órfão nenhum.
];

/** Aviso padrão pros cadastros sem tela — um texto só, pra não divergirem. */
export const AVISO_ENTIDADE_SEM_TELA =
  'Nenhuma tela desenha campos personalizados deste cadastro ainda. O campo fica salvo e ' +
  'aparece aqui na lista, mas ninguém consegue preenchê-lo pelo sistema.';

/**
 * 21 tipos suportados, agrupados pra UX no Step 1 do wizard.
 *
 * `icone` é um componente Lucide — o design system é Lucide-only, então o
 * tipo `LucideIcon` impede que volte emoji aqui (era ✏️📝✉️📞🆔🏢🔗🔢💰📅🕐⏰
 * até a FASE-C).
 */
export const TIPOS_DISPONIVEIS: ReadonlyArray<{
  value: FieldTipo;
  label: string;
  grupo: 'texto' | 'numerico' | 'datahora' | 'escolha' | 'avancado';
  icone: LucideIcon;
  hint: string;
}> = [
  { value: 'text', label: 'Texto curto', grupo: 'texto', icone: Type, hint: 'Até ~200 caracteres' },
  { value: 'textarea', label: 'Texto longo', grupo: 'texto', icone: AlignLeft, hint: 'Sem limite de tamanho' },
  { value: 'email', label: 'E-mail', grupo: 'texto', icone: Mail, hint: 'Confere se o e-mail é válido' },
  { value: 'phone', label: 'Telefone', grupo: 'texto', icone: Phone, hint: 'DDD + 8 ou 9 dígitos' },
  { value: 'cpf', label: 'CPF', grupo: 'texto', icone: Fingerprint, hint: 'Confere os dígitos' },
  { value: 'cnpj', label: 'CNPJ', grupo: 'texto', icone: Building2, hint: 'Confere os dígitos' },
  { value: 'url', label: 'Endereço de site', grupo: 'texto', icone: Link, hint: 'Ex.: https://site.com.br' },
  { value: 'number', label: 'Número', grupo: 'numerico', icone: Hash, hint: 'Inteiro ou com vírgula' },
  { value: 'currency', label: 'Dinheiro', grupo: 'numerico', icone: DollarSign, hint: 'Mostra como R$' },
  { value: 'percentage', label: 'Porcentagem', grupo: 'numerico', icone: Percent, hint: 'De 0 a 100' },
  { value: 'date', label: 'Data', grupo: 'datahora', icone: Calendar, hint: 'Dia, mês e ano' },
  { value: 'datetime', label: 'Data e hora', grupo: 'datahora', icone: CalendarClock, hint: 'Dia e horário' },
  { value: 'time', label: 'Hora', grupo: 'datahora', icone: Clock, hint: 'Só o horário' },
  { value: 'dropdown', label: 'Escolher uma', grupo: 'escolha', icone: ChevronDownSquare, hint: 'Lista pra escolher 1 opção' },
  { value: 'multi_select', label: 'Escolher várias', grupo: 'escolha', icone: ListChecks, hint: 'Permite marcar mais de uma' },
  { value: 'radio', label: 'Opções à vista', grupo: 'escolha', icone: CircleDot, hint: 'Todas as opções aparecem juntas' },
  { value: 'checkbox', label: 'Sim ou não', grupo: 'escolha', icone: CheckSquare, hint: 'Uma caixinha pra marcar' },
  { value: 'file', label: 'Arquivo', grupo: 'avancado', icone: Paperclip, hint: 'Anexar um arquivo' },
  { value: 'image', label: 'Imagem', grupo: 'avancado', icone: Image, hint: 'Anexar foto com miniatura' },
  { value: 'computed', label: 'Calculado', grupo: 'avancado', icone: Calculator, hint: 'O sistema preenche sozinho' },
  { value: 'json', label: 'Dados livres', grupo: 'avancado', icone: Braces, hint: 'Formato técnico, uso avançado' },
];

/**
 * FASE-C · regra "se não funciona, ou termina ou some": Arquivo e Imagem são
 * os únicos tipos sem caminho de ponta a ponta. O backend aceita e guarda a
 * chave do arquivo (`validator.ts` → file/image passam direto), mas NÃO existe
 * endpoint de upload pra campo personalizado — as únicas rotas de upload são
 * por entidade (`/deals/:id/documentos/upload-url`, `/projetos/:id/upload-url`).
 * Sem isso, quem escolhia esses tipos criava um campo impossível de preencher.
 *
 * Os dois continuam no `FieldTipo` e no `TIPOS_DISPONIVEIS` (o banco tem CHECK
 * com os 21 tipos, e campos já salvos dependem deles) — o que muda é só a
 * oferta no construtor. Campo JÁ salvo em Arquivo/Imagem reaparece SÓ na
 * edição dele, desabilitado e marcado "Em preparação", pra a tela não quebrar
 * nem trocar o tipo do campo sozinha.
 *
 * Pra tirar daqui: criar o endpoint de upload de campo personalizado e o
 * renderer correspondente em `DynamicField`.
 */
export const TIPOS_EM_PREPARACAO: ReadonlyArray<FieldTipo> = ['file', 'image'];

export const GRUPOS_TIPOS = [
  { value: 'texto', label: 'Texto' },
  { value: 'numerico', label: 'Numérico' },
  { value: 'datahora', label: 'Data/Hora' },
  { value: 'escolha', label: 'Escolha' },
  { value: 'avancado', label: 'Avançado' },
] as const;
